import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Avatar, Divider,
  Alert, CircularProgress, Button, Tooltip, Badge,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  List, ListItem, ListItemAvatar, ListItemText, IconButton,
  Card, CardContent, CardActions, LinearProgress,
} from '@mui/material';
import {
  Groups, CheckCircle, Cancel, BeachAccess, AccessTime,
  HomeWork, Receipt, MoreTime, Cake, WorkspacePremium,
  EmojiEvents, TrendingUp, Person, Refresh,
  NotificationsActive, ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(opts.headers || {}),
    },
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message || 'Error');
    return d;
  });
}

function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function avatarColor(name: string) {
  const colors = ['#1565c0', '#2e7d32', '#6a1b9a', '#e65100', '#00838f', '#880e4f', '#4527a0', '#c62828'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function timeAgo(dateStr: string) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

interface PendingItem {
  id: string; type: string; icon: React.ReactNode; color: string;
  employeeName: string; description: string; createdAt: string;
  path: string;
}

interface TeamMember {
  id: string; name: string; position?: string; department?: string;
  status?: string; clockedIn?: boolean;
}

interface UpcomingEvent {
  type: 'birthday' | 'anniversary' | 'leave' | 'probation';
  employeeName: string; date: string; detail: string; emoji: string;
}

export default function ManagerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const empId = (user as any)?.employee?.id;
  const empName = (user as any)?.employee
    ? `${(user as any).employee.firstName} ${(user as any).employee.lastName}`
    : 'Manager';

  const [team, setTeam] = useState<TeamMember[]>([]);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [teamAttendance, setTeamAttendance] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = useCallback(async () => {
    if (!empId) return;
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        apiFetch(`/api/employees/by-manager/${empId}`),           // 0 team
        apiFetch('/api/leave/requests/pending'),                  // 1 pending leave
        apiFetch('/api/expenses?status=pending&limit=20'),        // 2 pending expenses
        apiFetch('/api/overtime?status=pending&limit=20'),        // 3 pending OT
        apiFetch('/api/wfh?status=pending&limit=20'),             // 4 pending WFH
        apiFetch('/api/employees/milestones?days=14'),            // 5 upcoming milestones
        apiFetch('/api/attendance/team'),                         // 6 today's attendance
        apiFetch('/api/leave/requests?status=approved&limit=50'), // 7 approved leave (for on-leave-today)
        apiFetch('/api/toil/redemptions?status=pending&limit=20'),// 8 pending TOIL
      ]);

      // Team members
      const teamData = results[0].status === 'fulfilled' ? (results[0].value ?? []) : [];
      setTeam(teamData.map((e: any) => ({
        id: e.id,
        name: `${e.firstName} ${e.lastName}`,
        position: e.position?.title,
        department: e.department?.name,
      })));

      // Build pending approvals list
      const pending: PendingItem[] = [];
      const today = new Date().toISOString().split('T')[0];

      // Leave
      if (results[1].status === 'fulfilled') {
        (results[1].value ?? []).forEach((r: any) => {
          pending.push({
            id: r.id, type: 'Leave', icon: <BeachAccess />, color: '#1565c0',
            employeeName: r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : '?',
            description: `${r.leaveType?.name ?? 'Leave'} · ${r.startDate} → ${r.endDate}`,
            createdAt: r.createdAt, path: '/leave',
          });
        });
      }

      // Expenses
      if (results[2].status === 'fulfilled') {
        const expData = Array.isArray(results[2].value) ? results[2].value : (results[2].value?.data ?? []);
        expData.slice(0, 5).forEach((r: any) => {
          pending.push({
            id: r.id, type: 'Expense', icon: <Receipt />, color: '#1b5e20',
            employeeName: r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : '?',
            description: `${r.currency ?? 'HKD'} ${r.totalAmount ? Math.round(r.totalAmount).toLocaleString() : '—'} · ${r.title ?? r.description ?? 'Claim'}`,
            createdAt: r.createdAt, path: '/expenses',
          });
        });
      }

      // Overtime
      if (results[3].status === 'fulfilled') {
        const otData = Array.isArray(results[3].value) ? results[3].value : (results[3].value?.data ?? []);
        otData.slice(0, 5).forEach((r: any) => {
          pending.push({
            id: r.id, type: 'Overtime', icon: <MoreTime />, color: '#e65100',
            employeeName: r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : '?',
            description: `${r.date} · ${r.startTime}–${r.endTime}`,
            createdAt: r.createdAt, path: '/overtime',
          });
        });
      }

      // WFH
      if (results[4].status === 'fulfilled') {
        const wfhData = Array.isArray(results[4].value) ? results[4].value : (results[4].value?.data ?? []);
        wfhData.slice(0, 5).forEach((r: any) => {
          pending.push({
            id: r.id, type: 'WFH', icon: <HomeWork />, color: '#00838f',
            employeeName: r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : '?',
            description: `${r.date ?? r.startDate} · ${r.sessionType ?? 'Full day'}`,
            createdAt: r.createdAt, path: '/wfh',
          });
        });
      }

      // TOIL
      if (results[8].status === 'fulfilled') {
        (results[8].value ?? []).slice(0, 5).forEach((r: any) => {
          pending.push({
            id: r.id, type: 'TOIL', icon: <AccessTime />, color: '#4527a0',
            employeeName: r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : '?',
            description: `${r.hoursRequested}h · ${r.startDate} → ${r.endDate}`,
            createdAt: r.createdAt, path: '/toil',
          });
        });
      }

      setPendingItems(pending.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));

      // Upcoming events
      const events: UpcomingEvent[] = [];
      if (results[5].status === 'fulfilled') {
        (results[5].value?.upcoming ?? results[5].value ?? []).slice(0, 8).forEach((m: any) => {
          events.push({
            type: m.milestoneType === 'birthday' ? 'birthday' : 'anniversary',
            employeeName: m.employeeName ?? `${m.employee?.firstName ?? ''} ${m.employee?.lastName ?? ''}`.trim(),
            date: m.date ?? m.milestoneDate,
            detail: m.milestoneType === 'birthday' ? '🎂 Birthday' : `🏆 ${m.years ?? m.yearsOfService}yr Anniversary`,
            emoji: m.milestoneType === 'birthday' ? '🎂' : '🏆',
          });
        });
      }
      // On leave today from approved requests
      if (results[7].status === 'fulfilled') {
        const allLeave = Array.isArray(results[7].value) ? results[7].value : (results[7].value?.data ?? []);
        allLeave
          .filter((r: any) => r.startDate <= today && r.endDate >= today)
          .slice(0, 5)
          .forEach((r: any) => {
            events.push({
              type: 'leave',
              employeeName: r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : '?',
              date: today,
              detail: `${r.leaveType?.name ?? 'Leave'} until ${r.endDate}`,
              emoji: '🌴',
            });
          });
      }
      setUpcomingEvents(events);

      // Team attendance
      if (results[6].status === 'fulfilled') {
        setTeamAttendance(results[6].value ?? []);
      }

    } catch (e) {
      // Silently fail — partial data is fine for a dashboard
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toLocaleDateString('en-HK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const onLeaveToday = upcomingEvents.filter(e => e.type === 'leave');
  const milestones = upcomingEvents.filter(e => e.type !== 'leave');

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            👋 Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {(user as any)?.employee?.firstName ?? 'Manager'}
          </Typography>
          <Typography variant="body2" color="text.secondary">{today}</Typography>
        </Box>
        <Tooltip title={`Last refreshed ${lastRefresh.toLocaleTimeString()}`}>
          <IconButton onClick={load} size="small"><Refresh fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>
          {/* ── Top KPI row ────────────────────────────────────────────── */}
          <Grid size={12}>
            <Grid container spacing={2}>
              {[
                { label: 'Direct Reports', value: team.length, color: '#1565c0', icon: <Groups />, path: '/directory' },
                { label: 'Pending Approvals', value: pendingItems.length, color: pendingItems.length > 0 ? '#e65100' : '#2e7d32', icon: <NotificationsActive />, path: '/leave' },
                { label: 'On Leave Today', value: onLeaveToday.length, color: '#6a1b9a', icon: <BeachAccess />, path: '/leave' },
                { label: 'Clocked In Today', value: teamAttendance.filter((a: any) => a.status === 'present' || a.clockInTime).length, color: '#2e7d32', icon: <CheckCircle />, path: '/attendance' },
              ].map(kpi => (
                <Grid size={{ xs: 6, sm: 3 }} key={kpi.label}>
                  <Paper
                    onClick={() => navigate(kpi.path)}
                    sx={{
                      p: 2, textAlign: 'center', cursor: 'pointer',
                      borderTop: `3px solid ${kpi.color}`,
                      '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box sx={{ color: kpi.color, mb: 0.5 }}>{kpi.icon}</Box>
                    <Typography variant="h4" fontWeight={700} color={kpi.color}>{kpi.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* ── Pending Approvals ───────────────────────────────────────── */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ height: '100%' }}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  ⏳ Pending Approvals
                  {pendingItems.length > 0 && (
                    <Chip size="small" label={pendingItems.length} color="warning" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
              {pendingItems.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <CheckCircle sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography>All caught up! No pending approvals.</Typography>
                </Box>
              ) : (
                <List dense sx={{ maxHeight: 340, overflow: 'auto' }}>
                  {pendingItems.map((item, i) => (
                    <React.Fragment key={item.id}>
                      <ListItem
                        secondaryAction={
                          <Tooltip title={`Go to ${item.type}`}>
                            <IconButton edge="end" size="small" onClick={() => navigate(item.path)}>
                              <ArrowForward fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: item.color + '20', color: item.color, width: 36, height: 36 }}>
                            {item.icon}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={600}>{item.employeeName}</Typography>
                              <Chip size="small" label={item.type}
                                sx={{ bgcolor: item.color + '15', color: item.color, fontSize: '0.65rem', height: 18 }} />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                              <Typography variant="caption" color="text.secondary">{timeAgo(item.createdAt)}</Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {i < pendingItems.length - 1 && <Divider variant="inset" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* ── Team Members ────────────────────────────────────────────── */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ height: '100%' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={700}>👥 My Team ({team.length})</Typography>
                <Button size="small" onClick={() => navigate('/directory')} endIcon={<ArrowForward />}>Directory</Button>
              </Box>
              <List dense sx={{ maxHeight: 340, overflow: 'auto' }}>
                {team.length === 0 && (
                  <ListItem><ListItemText primary="No direct reports found" /></ListItem>
                )}
                {team.map((member, i) => {
                  const onLeave = onLeaveToday.some(e => e.employeeName === member.name);
                  const attendance = teamAttendance.find((a: any) =>
                    a.employeeId === member.id || a.employee?.id === member.id);
                  const clockedIn = attendance?.clockInTime && !attendance?.clockOutTime;
                  return (
                    <React.Fragment key={member.id}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: avatarColor(member.name), width: 36, height: 36, fontSize: '0.85rem' }}>
                            {initials(member.name)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={600}>{member.name}</Typography>}
                          secondary={<Typography variant="caption" color="text.secondary">{member.position ?? member.department ?? '—'}</Typography>}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {onLeave && <Chip size="small" label="On Leave" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem', height: 18 }} />}
                          {!onLeave && clockedIn && <Chip size="small" label="In" color="success" sx={{ fontSize: '0.65rem', height: 18 }} />}
                        </Box>
                      </ListItem>
                      {i < team.length - 1 && <Divider variant="inset" />}
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>

          {/* ── Upcoming Events ─────────────────────────────────────────── */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper>
              <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                <Typography variant="subtitle1" fontWeight={700}>📅 Coming Up (14 Days)</Typography>
              </Box>
              {milestones.length === 0 && onLeaveToday.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">Nothing coming up in the next 14 days</Typography>
                </Box>
              ) : (
                <List dense>
                  {onLeaveToday.map((e, i) => (
                    <ListItem key={`leave-${i}`}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1565c0', width: 32, height: 32, fontSize: '1rem' }}>
                          {e.emoji}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="body2" fontWeight={600}>{e.employeeName}</Typography>}
                        secondary={<Typography variant="caption" color="text.secondary">{e.detail}</Typography>}
                      />
                      <Chip size="small" label="Today" color="primary" sx={{ fontSize: '0.65rem', height: 18 }} />
                    </ListItem>
                  ))}
                  {milestones.map((e, i) => (
                    <ListItem key={`mile-${i}`}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#fff8e1', color: '#f57f17', width: 32, height: 32, fontSize: '1rem' }}>
                          {e.emoji}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="body2" fontWeight={600}>{e.employeeName}</Typography>}
                        secondary={<Typography variant="caption" color="text.secondary">{e.detail} · {e.date}</Typography>}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>


          {/* ── Quick Actions ────────────────────────────────────────────── */}
          <Grid size={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>⚡ Quick Actions</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[
                  { label: 'Approve Leave', path: '/leave', color: '#1565c0', icon: <BeachAccess /> },
                  { label: 'Approve Expenses', path: '/expenses', color: '#1b5e20', icon: <Receipt /> },
                  { label: 'Approve Overtime', path: '/overtime', color: '#e65100', icon: <MoreTime /> },
                  { label: 'Approve WFH', path: '/wfh', color: '#00838f', icon: <HomeWork /> },
                  { label: 'Give Kudos', path: '/recognition', color: '#f57f17', icon: <EmojiEvents /> },
                  { label: 'Org Changes', path: '/org-changes', color: '#880e4f', icon: <TrendingUp /> },
                  { label: 'Goals & OKRs', path: '/goals', color: '#4527a0', icon: <WorkspacePremium /> },
                  { label: 'Team Directory', path: '/directory', color: '#546e7a', icon: <Person /> },
                ].map(action => (
                  <Button
                    key={action.label}
                    variant="outlined"
                    startIcon={action.icon}
                    onClick={() => navigate(action.path)}
                    size="small"
                    sx={{
                      borderColor: action.color, color: action.color,
                      '&:hover': { bgcolor: action.color + '10', borderColor: action.color },
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
